import { useCallback, useEffect, useRef, useState } from 'react'
import styled, { css, keyframes } from 'styled-components'
import useDebounce from '../../hooks/useDebounce'
import Library from '../../Library'

/* ================= styles ================= */

const Wrapper = styled.div`
  position: relative;
  width: 100%;
`

const InputWrapper = styled.div`
  display: flex;
  position: relative;
  align-items: center;
  gap: 8px;
  width: 100%;
`

const Input = styled.input`
  background: transparent;
  border: none;
  outline: none;
  width: 100%;
  font-size: 16px;
  padding: 0;
  color: var(--input-text-color);

  ${({ disabled }) =>
    disabled &&
    css`
      color: var(--text-color-disabled);
      cursor: default;
    `}

  ${({ readOnly, disabled }) =>
    readOnly && !disabled &&
    css`
      background-color: var(--input-read-only-bg);
    `}

  ${({ $forceDarkMode }) =>
    $forceDarkMode &&
    css`
      html[data-theme='light'] & {
        color: var(--text-inverted-color);
      }
    `}

  ${({ $textOverflow }) =>
    $textOverflow &&
    css`
      overflow: hidden;
      text-overflow: ellipsis;
    `}
`

const IconButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;

  svg {
    fill: var(--input-element-color);
    transition: fill 0.2s;
  }

  &:hover:not(:disabled) svg {
    fill: var(--input-focused-color);
  }

  ${({ disabled }) =>
    disabled &&
    css`
      cursor: default;
      svg {
        fill: var(--color-disabled);
      }
    `}
`

/* ================= loading ================= */

const dotAnimation = keyframes`
  0% { transform: scale(0); }
  50% { transform: scale(1); }
  100% { transform: scale(0); }
`

const LoadingIndicator = styled.div`
  position: absolute;
  right: 0;
  top: -6px;
  font-size: 4px;

  span {
    animation: ${dotAnimation} 1s ease-in-out infinite;
    background-color: currentColor;
    border-radius: 50%;
    display: inline-block;
    height: 1em;
    width: 1em;
    margin-left: 1em;
  }

  span:nth-child(2) { animation-delay: 160ms; }
  span:nth-child(3) { animation-delay: 320ms; }
`

/* ================= icons ================= */

const EyeDisabled = () => (
  <svg viewBox='0 0 512 512' width='22' height='22'>
    <path d='M256 128c-88 0-168 48-211 128 43 80 123 128 211 128s168-48 211-128c-43-80-123-128-211-128zm0 192a64 64 0 110-128 64 64 0 010 128z' />
    <line x1='32' y1='480' x2='480' y2='32' stroke='currentColor' strokeWidth='40' />
  </svg>
)

const EyeEnabled = () => (
  <svg viewBox='0 0 512 512' width='22' height='22'>
    <path d='M256 128c-88 0-168 48-211 128 43 80 123 128 211 128s168-48 211-128c-43-80-123-128-211-128zm0 192a64 64 0 110-128 64 64 0 010 128z' />
  </svg>
)

const ClearIcon = () => (
  <svg viewBox='0 0 20 20' width='18' height='18'>
    <path d='M14.348 14.849a1.2 1.2 0 01-1.697 0L10 11.818l-2.651 3.03a1.2 1.2 0 01-1.697-1.697l2.759-3.152-2.759-3.152a1.2 1.2 0 011.697-1.697L10 8.182l2.651-3.03a1.2 1.2 0 011.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 010 1.698z' />
  </svg>
)

/* ================= highlight ================= */

const HighlightedText = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none; // клики проходят к input
  white-space: pre-wrap;
  overflow-wrap: break-word;
  font-size: 16px;      // совпадает с input
  line-height: normal;  // совпадает с input
  padding: 0;           // совпадает с input
  text-align: left;
`

/* ================= component ================= */

export default function SimpleTextInput(props) {
  const {
    type = 'text',
    name,
    id,
    value,
    disabled,
    readOnly,
    onChange,
    onFocus,
    onBlur,
    onKeyDown,
    debounceDelay,
    isClearable = false,
    forceDarkMode = false,
    textOverflow = true,
    autoComplete = 'off',
    searchValue,
    ...rest
  } = props

  const [debouncedHandleChange, isDebouncing] = useDebounce(onChange, debounceDelay)
  const [inputValue, setInputValue] = useState(value || '')
  const [passwordType, setPasswordType] = useState('password')
  const [prevInputValuesMap, setPrevInputValuesMap] = useState(new Map())

  const inputRef = useRef(null)
  const focusedNameRef = useRef(null)

  useEffect(() => {
    setInputValue(value || '')
  }, [value])

  const handleChange = useCallback(
    (e) => {
      setInputValue(e.target.value)

      if (onChange) {
        debounceDelay ? debouncedHandleChange(e) : onChange(e)
      }
    },
    [onChange, debounceDelay, debouncedHandleChange]
  )

  const handleClear = () => {
    if (readOnly || disabled) return

    setPrevInputValuesMap((prev) => {
      const map = new Map(prev)
      map.set(name, [...(map.get(name) || []), inputValue])
      return map
    })

    setInputValue('')
    onChange?.({ target: { name, value: '' } })
  }

  const handleUndo = useCallback(
    (e) => {
      if (
        e.ctrlKey &&
        e.code === 'KeyZ' &&
        document.activeElement === inputRef.current &&
        prevInputValuesMap.has(focusedNameRef.current)
      ) {
        const values = prevInputValuesMap.get(focusedNameRef.current)
        const restored = values.pop()
        if (restored !== undefined) {
          setInputValue(restored)
          onChange?.({ target: { name, value: restored } })
        }
      }
    },
    [prevInputValuesMap, onChange, name]
  )

  useEffect(() => {
    document.addEventListener('keydown', handleUndo)
    return () => document.removeEventListener('keydown', handleUndo)
  }, [handleUndo])

  return (
    <Wrapper>
      {isDebouncing && (
        <LoadingIndicator>
          <span /><span /><span />
        </LoadingIndicator>
      )}

      <InputWrapper>
        {searchValue && inputValue.toLowerCase().includes(searchValue.toLowerCase()) && (
          <HighlightedText>
            {Library.highlight(inputValue, searchValue)}
          </HighlightedText>
        )}
        <Input
          ref={inputRef}
          id={id}
          name={name}
          type={type === 'password' ? passwordType : type}
          value={inputValue}
          autoComplete={autoComplete}
          disabled={disabled}
          readOnly={readOnly}
          onChange={handleChange}
          onFocus={(e) => {
            focusedNameRef.current = name
            onFocus?.(e)
          }}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          $forceDarkMode={forceDarkMode}
          $textOverflow={textOverflow}
          {...rest}
        />

        {isClearable && inputValue && !disabled && !readOnly && (
          <IconButton onClick={handleClear}>
            <ClearIcon />
          </IconButton>
        )}

        {type === 'password' && !disabled && !readOnly && (
          <IconButton
            onClick={() =>
              setPasswordType(passwordType === 'password' ? 'text' : 'password')
            }
          >
            {passwordType === 'password' ? <EyeDisabled /> : <EyeEnabled />}
          </IconButton>
        )}
      </InputWrapper>
    </Wrapper>
  )
}
